import React from 'react';
import {Box, Stack, Tab, Tabs} from '@mui/material';
import PropTypes from 'prop-types';
import InfoHover from '../../../shared-components/InfoHover';

// AD-2737: the School Settings tabs are spread evenly across the page only on a large screen (1200px and wider). On a
// smaller screen each tab keeps its full name and the row scrolls sideways, as on the Forms page.
export const SETTINGS_TAB_SX = {px: {lg: 1.5}, minWidth: {lg: 0}, flex: {lg: '1 1 auto'}};

// Rounded buttons under the School Settings tabs, as on Manage Access, so they do not look like the tabs above.
export const PILLS_SX = {
    minHeight: 0,
    '& .MuiTab-root': {
        minHeight: 30,
        height: 30,
        my: 0.5,
        mr: 1,
        px: 2,
        py: 0,
        fontSize: 13,
        textTransform: 'none',
        fontWeight: 600,
        color: '#0A4F45',
        backgroundColor: '#fff',
        border: '1px solid #9fd4c7',
        borderRadius: '15px'
    },
    '& .MuiTab-root.Mui-selected': {color: '#fff', backgroundColor: '#0A8572', borderColor: '#0A8572'},
    // the 'i' in the open button is white, so it shows on the green
    '& .MuiTab-root.Mui-selected .MuiSvgIcon-root': {color: '#fff'}
};

// hovers: the words shown by an 'i' inside a button, by the button's name (Maria, 20 October 2026)
const SettingsPills = ({value, options, onChange, testId, end, hovers = {}}) => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{minHeight: 58, mb: 0.5}}>
        <Tabs
            value={value}
            onChange={(e, next) => onChange(next)}
            TabIndicatorProps={{sx: {display: 'none'}}}
            variant="scrollable"
            scrollButtons={false}
            sx={PILLS_SX}
            data-testid={testId}
        >
            {options.map(name => (
                <Tab
                    key={name}
                    value={name}
                    label={hovers[name]
                        ? (
                            <Box component="span" sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5}}>
                                {name}
                                <InfoHover text={hovers[name]} testId={`${testId}-${name.toLowerCase().replace(/ /g, '-')}-hover`}/>
                            </Box>
                        )
                        : name}
                    data-testid={`${testId}-${name.toLowerCase().replace(/ /g, '-')}`}
                />
            ))}
        </Tabs>
        {end}
    </Stack>
);

SettingsPills.propTypes = {
    value: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.string).isRequired,
    onChange: PropTypes.func.isRequired,
    testId: PropTypes.string,
    end: PropTypes.node,
    hovers: PropTypes.object
};

export default SettingsPills;

// Maria, 20 October 2026: the Manage Users tables in the style of the Forms tables: a light green header, shorter rows,
// and the table's icons on the row of buttons above, so there is no empty band over the table
// the small blue button used on the row above a settings table
export const SMALL_BLUE_BUTTON_SX = {
    backgroundColor: '#186BBE',
    boxShadow: 'none',
    height: 32,
    py: 0,
    px: 1.25,
    fontSize: 12,
    '&:hover': {backgroundColor: '#155FA7', boxShadow: 'none'}
};

// the row above a settings table, the height the table's icons are moved up by
// Maria, 20 October 2026: a little taller, so the boxes on it have room for their labels
export const SETTINGS_TABLE_TOP_ROW_SX = {minHeight: 58, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1.5};

// 'right' leaves room for a button at the end of the row of buttons
// AD-2736: 'rowsHeight' makes the table as tall as its rows. The Manage Users lists are in a box that is not stretched
// to the page, so without it their table had no height on screens bigger than a phone.
export const settingsTableProps = (compact, {right = 0, rowsHeight = false} = {}) => ({
    ...compact,
    ...(rowsHeight ? {muiTableContainerProps: {...compact.muiTableContainerProps, sx: {flex: '0 0 auto'}}} : {}),
    muiTopToolbarProps: {
        // the Profile Type box on Parent Invitations as short as the buttons
        // on a phone the icons stay above the table, as the buttons above go onto more than one line
        // AD-2781: overflow visible, so the label above a box on this row ('Filters' on Sync Logs) is shown in full
        sx: {...compact.muiTopToolbarProps.sx, position: {xs: 'static', sm: 'absolute'}, top: {sm: -54}, right, alignItems: 'center', overflow: 'visible', '& .MuiInputBase-root': {height: 32, backgroundColor: '#fff'}, '& .MuiFormControl-root': {my: 0}, '& .MuiButton-root': {height: 32, py: 0, whiteSpace: 'nowrap'}}
    },
    // Maria, 20 October 2026: shorter rows: less padding in every cell, and a smaller button for the row's menu
    muiTablePaperProps: {
        sx: {
            ...(rowsHeight ? {display: 'block', flexGrow: 0, height: 'auto'} : {}),
            overflow: 'visible',
            position: 'relative',
            '& tbody td.MuiTableCell-root': {paddingTop: '3px', paddingBottom: '3px'},
            // every row the same height on the three tables, so User Invitations is not tighter than the others
            '& tbody tr': {height: 38},
            '& tbody .MuiIconButton-root': {p: '3px'},
            // the tick box column's heading in the same light green
            '& thead th.MuiTableCell-root': {backgroundColor: '#e3f5f1'}
        }
    }
});
